import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/context/AuthContext';
import AuthBootLoader from '@/components/loaders/AuthBootLoader';
import {
    clearLocalStorage,
    getEncrypted,
    isExternalLogoutRedirect,
    removeFromLocalStorage,
    resolveLogoutPath,
} from '@/utils/functions/helperFunctions';
import { ExpireToken } from '@/services/auth.service';
import { getToken } from '@/utils/functions/TokenFunction';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(getToken() ?? null);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const navigate = useNavigate();

    const saveToken = (userToken: string) => {
        localStorage.setItem('token', JSON.stringify(getEncrypted(userToken)));
        setToken(userToken);
    };

    const clearToken = () => {
        removeFromLocalStorage('token');
        setToken(null);
    };

    const finishLogout = (redirect?: string | null) => {
        clearLocalStorage();
        setToken(null);

        if (redirect && isExternalLogoutRedirect(redirect)) {
            window.location.href = redirect;
            return;
        }

        navigate(resolveLogoutPath(redirect), { replace: true });
    };

    const logOut = async () => {
        setIsLoggingOut(true);
        try {
            const result = await ExpireToken();
            finishLogout(result?.redirect);
        } finally {
            setIsLoggingOut(false);
        }
    };

    return (
        <AuthContext.Provider value={{ token, setToken, saveToken, clearToken, logOut, isLoggingOut }}>
            {isLoggingOut ? <AuthBootLoader /> : null}
            {children}
        </AuthContext.Provider>
    );
};

export { useAuth } from '@/context/AuthContext';
